import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { GameProvider, useGameContext } from './contexts/GameContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { UserPreferencesProvider } from './contexts/UserPreferencesContext';
import { ToastProvider } from './hooks/useToast';
import LandingScreen from './screens/LandingScreen';
import ScenarioSelectionScreen from './screens/ScenarioSelectionScreen';
import CEOScenarioScreen from './components/CEOScenarioScreen';
import { InvestmentSelectionScreen } from './screens/InvestmentSelectionScreen';
import { IncidentScreen } from './screens/IncidentScreen';
import FinalScoringScreen from './screens/FinalScoringScreen';
import { ScenarioManagementRoutes } from './modules/scenarioManagement/routes';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ErrorBoundaryDebug } from './components/ErrorBoundaryDebug';
import { ThemeToggle } from './components/ThemeToggle';
import { logger } from './services/LoggingService';
import {
  loadingContainerStyle,
  loadingTextStyle,
  errorContainerStyle,
  errorContainerWithTextAlignStyle,
  errorHeadingStyle,
  errorMessageStyle,
  errorButtonStyle,
  themeToggleContainerStyle
} from './App.styles';
import './App.css';

// Route guard component to ensure proper flow
const RouteGuard: React.FC<{
  children: React.ReactNode;
  allowedPhases: string[];
  routePath?: string;
}> = ({ children, allowedPhases, routePath }) => {
  const { gameState } = useGameContext();

  // Special case: landing page (/) and scenarios page (/scenarios) always allowed when in scenario-selection phase
  if ((routePath === '/' || routePath === '/scenarios') && (gameState.gamePhase === 'scenario-selection' || !gameState.gamePhase)) {
    return <>{children}</>;
  }

  // Check if current phase is allowed for this route
  if (!allowedPhases.includes(gameState.gamePhase)) {
    // Map game phase to route
    const phaseRouteMap: Record<string, string> = {
      'scenario-selection': '/scenarios',
      'intro': '/intro',
      'investments': '/investments',
      'gameplay': '/incidents',
      'scoring': '/scoring'
    };

    const targetPath = phaseRouteMap[gameState.gamePhase] || '/';

    // Only redirect if we're not already on the target path
    if (routePath !== targetPath) {
      return <Navigate to={targetPath} replace />;
    }
  }

  return <>{children}</>;
};

function AppContent() {
  const [isInitialized, setIsInitialized] = React.useState(false);
  
  React.useEffect(() => {
    // Give the app a moment to initialize
    const timer = setTimeout(() => {
      setIsInitialized(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);
  
  if (!isInitialized) {
    return (
      <div style={loadingContainerStyle}>
        <div style={loadingTextStyle}>Loading...</div>
      </div>
    );
  }
  
  return (
    <Routes>
      <Route
        path="/"
        element={
          <RouteGuard allowedPhases={['scenario-selection']} routePath="/">
            <LandingScreen />
          </RouteGuard>
        }
      />
      <Route
        path="/scenarios"
        element={
          <RouteGuard allowedPhases={['scenario-selection']} routePath="/scenarios">
            <ScenarioSelectionScreen />
          </RouteGuard>
        }
      />
      <Route
        path="/intro"
        element={
          <RouteGuard allowedPhases={['intro']} routePath="/intro">
            <CEOScenarioScreen />
          </RouteGuard>
        }
      />
      <Route 
        path="/investments" 
        element={
          <RouteGuard allowedPhases={['investments']} routePath="/investments">
            <InvestmentSelectionScreen />
          </RouteGuard>
        } 
      />
      <Route 
        path="/incidents" 
        element={
          <RouteGuard allowedPhases={['gameplay']} routePath="/incidents">
            <IncidentScreen />
          </RouteGuard>
        } 
      />
      <Route
        path="/scoring"
        element={
          <RouteGuard allowedPhases={['scoring']} routePath="/scoring">
            <FinalScoringScreen />
          </RouteGuard>
        }
      />
      {/* Scenario Management Routes - No phase guard needed as it's management functionality */}
      <Route path="/manage/*" element={<ScenarioManagementRoutes />} />
      
      {/* Fallback route for unmatched paths */}
      <Route 
        path="*" 
        element={
          <div style={errorContainerStyle}>
            <h1 style={errorHeadingStyle}>Page Not Found</h1>
            <p style={errorMessageStyle}>The page you're looking for doesn't exist.</p>
            <button
              onClick={() => window.location.href = '/'}
              style={errorButtonStyle}
            >
              Go to Home
            </button>
          </div>
        }
      />
    </Routes>
  );
}

function App() {
  const [hasError, setHasError] = React.useState(false);
  
  React.useEffect(() => {
    // Log when app mounts
    logger.info('Application mounted successfully', 'App');
    
    // Global error handler
    const handleError = (event: ErrorEvent) => {
      logger.error('Global unhandled error occurred', 'App', {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      }, event.error);
      setHasError(true);
    };
    
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);
  
  if (hasError) {
    return (
      <div style={errorContainerWithTextAlignStyle}>
        <h1 style={errorHeadingStyle}>Something went wrong</h1>
        <p style={errorMessageStyle}>An error occurred while loading the application.</p>
        <button
          onClick={() => {
            setHasError(false);
            window.location.reload();
          }}
          style={errorButtonStyle}
        >
          Reload Application
        </button>
      </div>
    );
  }
  
  return (
    <Router>
      <ErrorBoundaryDebug>
        <ErrorBoundary
          enableRecovery={true}
          showDetails={process.env.NODE_ENV === 'development'}
          component="App"
          onError={(error, errorInfo) => {
            logger.error('ErrorBoundary caught an error', 'App', {
              componentStack: errorInfo.componentStack
            }, error);
          }}
        >
          <ThemeProvider defaultTheme="dark">
            <UserPreferencesProvider>
              <GameProvider>
                <ToastProvider position="bottom-right">
                  <div className="App">
                    <AppContent />
                    <div style={themeToggleContainerStyle}>
                      <ThemeToggle size="lg" />
                    </div>
                  </div>
                </ToastProvider>
              </GameProvider>
            </UserPreferencesProvider>
          </ThemeProvider>
        </ErrorBoundary>
      </ErrorBoundaryDebug>
    </Router>
  );
}

export default App;